/**
 * ZBA Social Booking Form — /fb and /tg routes
 * Multilingual (DE/UK/RU), with qualification step + full tracking
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ProgressIndicator from 'components/ui/ProgressIndicator';
import StepNavigation from 'components/ui/StepNavigation';
import SocialStepConsultationType from './components/SocialStepConsultationType';
import StepCalendar from '../multi-step-booking-form/components/StepCalendar';
import StepTimeSlot from '../multi-step-booking-form/components/StepTimeSlot';
import StepContactForm from '../multi-step-booking-form/components/StepContactForm';
import StepSummary from '../multi-step-booking-form/components/StepSummary';
import StepSuccess from '../multi-step-booking-form/components/StepSuccess';
import BookingFooter from '../multi-step-booking-form/components/BookingFooter';
import LanguageSelector from './components/LanguageSelector';
import Qualification from './components/Qualification';
import { checkSlotStillAvailable, BERATER } from 'utils/graphApi';
import { insertBookingLog } from '../../services/apiService';
import { T } from '../../i18n/social';

const TOTAL_STEPS = 8;
function makeBadge(n, lang) {
  if (lang === 'uk') return `Крок ${n} з ${TOTAL_STEPS}`;
  if (lang === 'ru') return `Шаг ${n} из ${TOTAL_STEPS}`;
  return `Schritt ${n} von ${TOTAL_STEPS}`;
}

function localDateStr(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Step indices: 0=lang, 1=qual, 2=type, 3=calendar, 4=timeslot, 5=contact, 6=summary, 7=success

export default function SocialBookingForm({ channel }) {
  const location = useLocation();

  // ── Phase 1: language ──────────────────────────────────────────────────────
  const [lang, setLang] = useState(null); // null = not chosen yet

  // ── Phase 2: qualification ─────────────────────────────────────────────────
  const [qualData, setQualData] = useState(null); // null = not done

  // ── Phase 3: booking steps ─────────────────────────────────────────────────
  const [step, setStep] = useState(1); // 1=type, 2=cal, 3=time, 4=contact, 5=summary, 6=success
  const [consultationType, setConsultationType]     = useState(null);
  const [selectedDate, setSelectedDate]             = useState(null);
  const [selectedSlot, setSelectedSlot]             = useState(null);
  const [selectedBeraterEmail, setSelectedBeraterEmail] = useState(null);
  const [formData, setFormData] = useState({
    vorname: '', nachname: '', email: '', telefon: '', telefonPrefix: '+49', wohnort: '',
  });
  const [isLoading, setIsLoading]   = useState(false);
  const [slotConflict, setSlotConflict] = useState(false);
  const [duplicateBooking, setDuplicateBooking] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);

  // ── Tracking ───────────────────────────────────────────────────────────────
  const [utmParams, setUtmParams] = useState({
    utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', utm_term: '',
  });
  const [referrerUrl, setReferrerUrl] = useState('');
  const sessionId = useRef(`sess_${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const content  = params.get('utm_content')  || '';
    const term     = params.get('utm_term')     || '';
    // If utm_content has a chatId but utm_term (group name) is missing, use content as label
    const resolvedTerm = term || (content ? `tg_${content.slice(-6)}` : '');
    setUtmParams({
      utm_source:   params.get('utm_source')   || channel,
      utm_medium:   params.get('utm_medium')   || 'social',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content:  content,
      utm_term:     resolvedTerm,
    });
    setReferrerUrl(document.referrer || params.get('ref') || '');
  }, [location.search]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const goTo = (s) => setStep(s);

  const handleTypeSelect = (type) => { setConsultationType(type); setTimeout(() => goTo(2), 350); };
  const handleDateSelect = (date) => { setSelectedDate(date); setSelectedSlot(null); setSelectedBeraterEmail(null); setTimeout(() => goTo(3), 300); };
  const handleSlotSelect = (slot, beraterEmail) => {
    setSelectedSlot(slot);
    if (beraterEmail) setSelectedBeraterEmail(beraterEmail);
    setTimeout(() => goTo(4), 400);
  };

  const handleBook = async () => {
    setIsLoading(true);
    setSlotConflict(false);

    try {
      const stillAvailable = await checkSlotStillAvailable(
        localDateStr(selectedDate), selectedSlot,
        consultationType === 'onsite' ? 60 : 30, selectedBeraterEmail
      );
      if (!stillAvailable) { setSlotConflict(true); setIsLoading(false); return; }
    } catch (_) {}

    const datePart = localDateStr(selectedDate).replace(/-/g, '');
    const randPart = Math.random().toString(36).toUpperCase().slice(2, 6);
    const bookingRef = `ZBA-${datePart}-${randPart}`;

    const beraterObj = BERATER?.find(b => b?.email === selectedBeraterEmail);

    try {
      const body = {
        ...formData,
        telefon: `${(formData.telefonPrefix || '+49').trim()} ${(formData.telefon || '').replace(/^[+\s]+/, '')}`.trim(),
        type: consultationType,
        date: localDateStr(selectedDate),
        time: selectedSlot,
        beraterEmail: selectedBeraterEmail,
        beraterName: beraterObj?.name || '',
        booking_ref: bookingRef,
        source: channel, // 'fb' or 'tg'
        channel,
        language: lang,
        referrer_url: referrerUrl,
        deutschkenntnisse: qualData?.deutschkenntnisse || 'keine_angabe',
        fuehrerschein_b:   qualData?.fuehrerschein_b   || 'keine_angabe',
        ...utmParams,
      };

      const res = await fetch('/api/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.duplicate) {
        setDuplicateBooking(data);
      } else if (data.success) {
        setBookingResult({ ...data, bookingRef, beraterName: beraterObj?.name || '' });
        goTo(6);
      } else {
        // Unknown error - show generic message but reset loading
        console.error('[book] Failed:', data.error || 'unknown');
      }
    } catch (err) {
      console.error('[book] Network error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Phase 1: Language selection ────────────────────────────────────────────
  if (!lang) {
    return <LanguageSelector onSelect={setLang} />;
  }

  // ── Phase 2: Qualification ─────────────────────────────────────────────────
  if (!qualData) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page, #f0f2f5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <Qualification lang={lang} channel={channel} onComplete={setQualData} />
      </div>
    );
  }

  // ── Phase 3: Booking steps ─────────────────────────────────────────────────
  if (step === 6) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page, #f0f2f5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <StepSuccess
          formData={formData}
          selectedDate={selectedDate}
          selectedSlot={selectedSlot}
          consultationType={consultationType}
          bookingRef={bookingResult?.bookingRef}
          outlookLink={bookingResult?.webLink}
          lang={lang}
        />
      </div>
    );
  }

  const progressStep = step; // 1-5 maps to progress 1-5

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page, #f0f2f5)' }}>
      {step < 6 && step > 0 && (
        <ProgressIndicator currentStep={progressStep + 1} totalSteps={7} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', minHeight: '80vh' }}>
        {step === 1 && (
          <SocialStepConsultationType lang={lang} selected={consultationType} onSelect={handleTypeSelect} badge={makeBadge(4, lang)} />
        )}
        {step === 2 && (
          <StepCalendar selectedDate={selectedDate} onSelect={handleDateSelect}
            consultationType={consultationType} lang={lang} stepBadge={makeBadge(5, lang)} />
        )}
        {step === 3 && (
          <StepTimeSlot selectedDate={selectedDate} selectedSlot={selectedSlot}
            onSelect={handleSlotSelect} consultationType={consultationType}
            onBack={() => goTo(2)} lang={lang} stepBadge={makeBadge(6, lang)} />
        )}
        {step === 4 && (
          <StepContactForm formData={formData} onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            onNext={() => goTo(5)} onBack={() => goTo(3)} lang={lang} stepBadge={makeBadge(7, lang)} />
        )}
        {duplicateBooking && (
          <div className="booking-card" style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', fontWeight: 700, color: '#1e2b38', marginBottom: '12px' }}>
              {lang === 'uk' ? 'У вас вже є запис' : lang === 'ru' ? 'У вас уже есть запись' : 'Du hast bereits einen Termin'}
            </h2>
            <p style={{ color: '#475569', fontSize: '15px', lineHeight: 1.6, marginBottom: '24px' }}>
              {lang === 'uk' ? `Ваш запис: ${duplicateBooking.existing_date} о ${duplicateBooking.existing_time}` : lang === 'ru' ? `Ваша запись: ${duplicateBooking.existing_date} в ${duplicateBooking.existing_time}` : `Dein Termin: ${duplicateBooking.existing_date} um ${duplicateBooking.existing_time} Uhr`}
            </p>
            <a href={`tel:${(duplicateBooking.contact_phone || '').replace(/[^0-9+]/g, '')}`}
              style={{ display: 'inline-block', padding: '16px 32px', background: '#3E4954', color: '#D0E5F5', borderRadius: '14px', fontSize: '18px', fontWeight: 700, textDecoration: 'none' }}>
              📞 {duplicateBooking.contact_phone}
            </a>
          </div>
        )}

        {!duplicateBooking && step === 5 && (
          <StepSummary
            formData={formData} selectedDate={selectedDate} selectedSlot={selectedSlot}
            consultationType={consultationType} selectedBeraterEmail={selectedBeraterEmail}
            onBook={handleBook} onBack={() => goTo(4)}
            isLoading={isLoading} slotConflict={slotConflict}
            lang={lang} stepBadge={makeBadge(8, lang)}
          />
        )}
      </div>
      <BookingFooter />
    </div>
  );
}
